import React, { useMemo, useRef, useEffect, useState } from "react";
import { EditFieldGrid } from "../EditFieldGrid";
import { EditGroup } from "../EditGroup";
import { useEditUI } from "../../context/EditUIPreferencesContext";
import { buildGroupHierarchy, annotateVisibilityAndFilter, shouldShowField } from "../../utils/grouping";
import { getIcon } from "../../utils/icons";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";
import { ChevronDownIcon, ChevronUpIcon } from "../../../design-system/icons";
import { updateFlattenedNestedValue } from "../../utils/nestedValueUtils";


// Lightweight folder-style group renderer for nested editors to avoid circular deps
const NestedGroupFolder = ({ groupName, groupInfo = {}, fields, children = [], values, onChange, level = 1, parentFieldPath }) => {
  const titleRef = useRef(null);
  const [titleHeight, setTitleHeight] = useState(32);
  // Use local state so groups start open and aren't affected by individual field expansions
  const [isOpen, setIsOpen] = useState(true);
  // Sticky functionality removed - see GitHub issue: folders_view_sticky_titles_positioning.md
  useEffect(() => {
    if (titleRef.current) setTitleHeight(titleRef.current.offsetHeight);
  }, []);

  const visibleFields = useMemo(() => fields, [fields]);
  const hasChildren = Array.isArray(children) && children.length > 0;
  const hasFields = Array.isArray(visibleFields) && visibleFields.length > 0;

  const leftPadding = level * 8;
  const contentOffset = 6;
  const lineLeft = leftPadding + contentOffset + 8;

  return (
    <div className="relative mb-4" style={{ paddingLeft: `${leftPadding}px` }}>
      {(hasFields || hasChildren) && isOpen && (
        <div className="absolute w-0.5 bg-gray-300 dark:bg-gray-600" style={{ left: `${lineLeft}px`, top: `${titleHeight + 4}px`, bottom: 0 }} />
      )}
      <div className="relative" style={{ paddingLeft: `${contentOffset}px` }}>
        <div
          ref={titleRef}
          className={`flex items-center gap-2 mb-3 pb-2 border-b ${borders.default} ${typography.bodySmall} ${textColors.secondary} font-medium`}
        >
          {groupInfo.icon && (
            <span className={`${textColors.tertiary} flex-shrink-0`}>{getIcon(groupInfo.icon)}</span>
          )}
          <span>{groupInfo.title || groupName}</span>
          <button
            type="button"
            aria-label={isOpen ? "Collapse group" : "Expand group"}
            className={`ml-auto p-1 ${textColors.tertiary} hover:${textColors.primary}`}
            onClick={() => setIsOpen((prev) => !prev)}
          >
            {isOpen ? <ChevronUpIcon size="sm" /> : <ChevronDownIcon size="sm" />}
          </button>
        </div>
        {isOpen && (
        <EditFieldGrid
          className="mb-4 pl-5"
          fields={visibleFields}
          values={values}
          onChange={onChange}
          parentFieldPath={parentFieldPath}
        />
        )}
        {hasChildren && isOpen && (
          <div className="space-y-2 mt-2">
            {children.map((child) => (
              <NestedGroupFolder
                key={child.name}
                groupName={child.name}
                groupInfo={child.groupInfo}
                fields={child.fields}
                children={child.children}
                values={values}
                onChange={onChange}
                level={level + 1}
                parentFieldPath={parentFieldPath}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const NestedField = ({ field, value, metadata, onChange, parentFieldPath }) => {
  const nestedValues = value || {};
  const { groupViewMode } = useEditUI();
  const [isSimpleOpen, setIsSimpleOpen] = useState(
    metadata?.initially_compressed !== true,
  );
  const childFieldPath = parentFieldPath ? `${parentFieldPath}.${field}` : field;
  const appearanceTitle = String(metadata?.appearance_title || "").trim().toLowerCase();
  const isButtonTextField = field === "text" && appearanceTitle === "button text";
  const canRemoveNestedRelation =
    value &&
    typeof value === "object" &&
    value.id != null &&
    ["title", "text", "image", "button_collection"].includes(field) &&
    !isButtonTextField;

  // Group nested fields by their group property
  const groupedNestedFields = Object.entries(metadata.nested_fields).reduce(
    (groups, [nestedField, nestedMetadata]) => {
      const groupInfo = nestedMetadata.group || {};
      const groupName = groupInfo.name || "other";
      if (!groups[groupName]) {
        groups[groupName] = {
          groupInfo: groupInfo,
          fields: [],
        };
      }
      groups[groupName].fields.push({
        field: nestedField,
        metadata: nestedMetadata,
      });
      return groups;
    },
    {},
  );

  // Sort nested groups by their order, ensuring MAIN group (order=0) appears first
  const sortedNestedGroups = Object.entries(groupedNestedFields).sort(
    ([, a], [, b]) => {
      const orderA = a.groupInfo?.order ?? 999;
      const orderB = b.groupInfo?.order ?? 999;
      return orderA - orderB;
    },
  );

  // Visibility helpers (duplicated logic for nested context)
  const toBool = (val) => {
    if (typeof val === "boolean") return val;
    if (typeof val === "number") return val !== 0;
    if (typeof val === "string") {
      return !["false", "0", "", "null", "undefined", "none"].includes(
        val.toLowerCase(),
      );
    }
    return Boolean(val);
  };

  const evalCondition = (cond) => {
    if (!cond) return null;
    let result = null;
    if (typeof cond === "string") {
      result = toBool(nestedValues[cond]);
    } else if (typeof cond === "object") {
      result = Object.entries(cond).every(([field, required]) => {
        const val = nestedValues[field];
        if (required === null || required === undefined) {
          return val === null || val === undefined;
        }
        if (Array.isArray(required)) {
          return required.includes(val);
        }
        if (typeof required === "boolean") {
          return toBool(val) === required;
        }
        return val === required;
      });
    }
    return result;
  };

  const shouldShowField = (metadata) => {
    const { show_if, hide_if } = metadata;
    const showResult = evalCondition(show_if);
    const hideResult = evalCondition(hide_if);
    return !(showResult === false || hideResult === true);
  };

  // Pretty-label helper: CharacterStyle -> Character Style; image_style -> Image Style
  const pretty = (s) => {
    if (!s) return "";
    const spaced = String(s).replace(/_/g, " ");
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  // Render groups inline without the relation's wrapper title when any of the explicit Option D
  // primitives request it. ``flatten_in_folders`` is the deprecated alias kept for backward
  // compatibility with metadata that hasn't been migrated yet.
  const shouldFlatten = Boolean(
    metadata?.hide_relation_title || metadata?.inline_children || metadata?.flatten_in_folders,
  );
  const nestedTitleClassName = `${typography.bodyLarge} font-semibold ${textColors.primary}`;

  if (shouldFlatten) {
    // Render without title wrapper - just the groups directly without extra spacing wrapper
    return (
      <>
        {/* MAIN fields without wrapper - render directly */}
        {groupedNestedFields.main && (
          <EditFieldGrid
            fields={groupedNestedFields.main.fields}
            values={nestedValues}
            parentFieldPath={childFieldPath}
            onChange={(nestedFieldName, value) => {
              const updated = updateFlattenedNestedValue(nestedValues, nestedFieldName, value);
              onChange(field, updated);
            }}
          />
        )}
        
        {/* Build and render hierarchy once (exclude 'main') */}
        {(() => {
          const nodes = Object.entries(groupedNestedFields)
            .filter(([gName]) => gName !== 'main')
            .map(([gName, gData]) => ({
              name: gName,
              groupInfo: gData.groupInfo || {},
              fields: gData.fields || [],
              children: [],
            }))
            .sort((a, b) => (a.groupInfo?.order ?? 999) - (b.groupInfo?.order ?? 999));
          const filtered = annotateVisibilityAndFilter(nodes, nestedValues);
          return filtered.map((node) => (
            <NestedGroupFolder
              key={node.name}
              groupName={node.name}
              groupInfo={node.groupInfo}
              fields={node.fields}
              children={[]}
              values={nestedValues}
              onChange={(nf, v) => onChange(field, updateFlattenedNestedValue(nestedValues, nf, v))}
              level={1}
              parentFieldPath={childFieldPath}
            />
          ));
        })()}
      </>
    );
  }

  const onlyMainGroup =
    sortedNestedGroups.length === 1 && sortedNestedGroups[0]?.[0] === "main";

  if (groupViewMode !== "folders" && onlyMainGroup) {
    const mainGroupData = sortedNestedGroups[0][1];
    const visibleMainFields = mainGroupData.fields.filter(({ metadata: nestedMetadata }) =>
      shouldShowField(nestedMetadata),
    );
    const relationTitle = metadata?.appearance_title || pretty(field);
    const relationIcon = metadata?.group?.icon;

    if (visibleMainFields.length === 0) return null;

    return (
      <div className="space-y-3">
        <button
          type="button"
          className={`flex w-full items-center gap-2 border-b ${borders.default} pb-2 text-left ${typography.bodySmall} ${textColors.secondary} font-medium`}
          aria-expanded={isSimpleOpen}
          onClick={() => setIsSimpleOpen((open) => !open)}
        >
          {relationIcon ? (
            <span className={`${textColors.tertiary} flex-shrink-0`}>
              {getIcon(relationIcon)}
            </span>
          ) : null}
          <span className="min-w-0 flex-1">
            {relationTitle}
          </span>
          <span className={`${textColors.tertiary} flex-shrink-0 p-1`}>
            {isSimpleOpen ? <ChevronUpIcon size="sm" /> : <ChevronDownIcon size="sm" />}
          </span>
        </button>

        {isSimpleOpen ? (
          <EditFieldGrid
            className="pl-5"
            fields={visibleMainFields}
            values={nestedValues}
            parentFieldPath={childFieldPath}
            onChange={(nestedFieldName, nestedValue) => {
              onChange(
                field,
                updateFlattenedNestedValue(nestedValues, nestedFieldName, nestedValue),
              );
            }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header row with relation title and optional Remove */}
      <div className="flex items-center justify-between">
        <div className={nestedTitleClassName}>
          {metadata?.appearance_title || pretty(field)}
        </div>
        {/* Remove only for specific content fields */}
        {canRemoveNestedRelation && (
          <button
            type="button"
            className={`${typography.caption} px-2 py-1 rounded ${interactive.dangerBg} ${interactive.dangerHover} !text-white transition-colors`}
            onClick={() => onChange(field, null)}
            title={`Remove ${field}`}
          >
            Remove
          </button>
        )}
      </div>
      {groupViewMode === 'folders' ? (
        <>
          {/* MAIN fields */}
          {groupedNestedFields.main && (
            <EditFieldGrid
              fields={groupedNestedFields.main.fields}
              values={nestedValues}
              parentFieldPath={childFieldPath}
              onChange={(nestedFieldName, value) => {
                onChange(field, updateFlattenedNestedValue(nestedValues, nestedFieldName, value));
              }}
            />
          )}
          {/* Build and render hierarchy once (exclude 'main') */}
          {(() => {
            // Flatten nested groups: treat siblings (ignore father_group) so e.g.,
            // Typography and Text Styling appear at the same level.
            const nodes = Object.entries(groupedNestedFields)
              .filter(([gName]) => gName !== 'main')
              .map(([gName, gData]) => ({
                name: gName,
                groupInfo: gData.groupInfo || {},
                fields: gData.fields || [],
                children: [],
              }))
              .sort((a, b) => (a.groupInfo?.order ?? 999) - (b.groupInfo?.order ?? 999));
            const filtered = annotateVisibilityAndFilter(nodes, nestedValues);
            return (
              <div className="space-y-2">
                {filtered.map((node) => (
                  <NestedGroupFolder
                    key={node.name}
                    groupName={node.name}
                    groupInfo={node.groupInfo}
                    fields={node.fields}
                    children={[]}
                    values={nestedValues}
                    onChange={(nf, v) => onChange(field, updateFlattenedNestedValue(nestedValues, nf, v))}
                    level={1}
                    parentFieldPath={childFieldPath}
                  />
                ))}
              </div>
            );
          })()}
        </>
      ) : (
        sortedNestedGroups.map(([nestedGroupName, nestedGroupData]) => (
          nestedGroupName === 'main' ? (
            <EditFieldGrid
              key={nestedGroupName}
              fields={nestedGroupData.fields}
              values={nestedValues}
              parentFieldPath={childFieldPath}
              onChange={(nestedFieldName, value) => {
                onChange(field, updateFlattenedNestedValue(nestedValues, nestedFieldName, value));
              }}
            />
          ) : (
            <EditGroup
              key={nestedGroupName}
              groupName={nestedGroupName}
              groupInfo={nestedGroupData.groupInfo}
              fields={nestedGroupData.fields.filter(({ metadata }) => shouldShowField(metadata))}
              values={nestedValues}
              onChange={(nestedField, value) => {
                onChange(field, updateFlattenedNestedValue(nestedValues, nestedField, value));
              }}
              level={1}
              parentFieldPath={childFieldPath}
            />
          )
        ))
      )}
    </div>
  );
};

export { NestedField };
